import type { HeadTag, HeadTagName } from './HeadContext.ts';
import { reverse } from 'es-toolkit/compat';
import { createElement, Fragment, use } from 'react';
import flattenChildren from 'react-keyed-flatten-children';
import { HeadContext } from './HeadContext.ts';

const tagKeys: {
  [Tag in HeadTagName]?: Array<keyof React.ComponentPropsWithoutRef<Tag>>;
} = {
  link: ['rel', 'hrefLang', 'type', 'media', 'sizes'] as const,
  meta: ['charSet', 'httpEquiv', 'name', 'property'] as const
};

export function HeadContent() {
  const { groups } = use(HeadContext);

  const tagsToRender: HeadTag[] = [];
  let titleTag: HeadTag | undefined;

  // TODO: Use toReversed() once it has gained wider browser support
  reverse([...groups])
    .map(group => group.tags)
    .flat()
    .forEach(tag => {
      if (tag.tagName === 'title') {
        // Only keep the last <title> tag
        if (!titleTag) {
          titleTag = tag;
        }
      } else {
        // De-dupe <meta> and <link> tags
        if (!tagsToRender.find(existingTag => tagsHaveSameKey(tag, existingTag))) {
          tagsToRender.push(tag);
        }
      }
    });

  if (titleTag) {
    tagsToRender.unshift(titleTag);
  }

  const tagElements = tagsToRender.map(tag =>
    createElement<React.ComponentPropsWithoutRef<typeof tag.tagName>>(tag.tagName, tag.props)
  );

  return <Fragment>{flattenChildren(tagElements)}</Fragment>;
}

function tagsHaveSameKey(a: HeadTag, b: HeadTag): boolean {
  if (a.tagName !== b.tagName) {
    return false;
  }

  const keyA = reduceTagKeys(a.tagName, a.props);
  const keyB = reduceTagKeys(b.tagName, b.props);
  return !!keyA && keyA === keyB;
}

function reduceTagKeys(tagName: HeadTagName, props: Record<string, unknown>): string {
  return tagName in tagKeys && tagKeys[tagName]
    ? tagKeys[tagName]
        .map(attr => `${attr}=${String(props[attr])}`)
        .filter(Boolean)
        .join('|')
    : '';
}
